import { getEnv, navigate } from '@/common/agents';
import { useCommonStore } from '@/store/useCommonStore';
import { getRoomId } from '$/api/server';
import { ProjectType } from '$/api/server.types';
import { message } from '$/components/Message';
import { useOfficialStore } from '$/store/useOfficialStore';

const FLOW_INIT_MEDIA_KEY = 'FLOW_INIT_MEDIA_KEY';

interface CreateFlowRoomOptions {
  location: string;
  initMediaList?: { url: string; type: 'image' | 'video'; width?: number; height?: number; }[];
  openNewWindow?: boolean;
}

/**
 * 新建工作流
 *
 * @export
 * @param {string} location
 * @param {({ url: string; type: 'image' | 'video'; }[])} [initMediaList]
 * @return {*}
 */
export async function createNewFlow(options: CreateFlowRoomOptions | string) {
  const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
  const { toLogin } = useOfficialStore.getState();
  const { initUserInfo } = useCommonStore.getState();
  if (!isUserLoggedIn) {
    await toLogin().then(() => {
      initUserInfo();
    });
    createNewFlowFun(options);
    return;
  }
  createNewFlowFun(options);
}

/**
 * 新建工作流
 *
 * @param {string} location
 * @param {({ url: string; type: 'image' | 'video'; width?: number; height?: number; }[])} [initMediaList]
 */
async function createNewFlowFun(options: CreateFlowRoomOptions | string) {
  const normalized = typeof options === 'string' ? { location: options } : options;
  const { location, initMediaList, openNewWindow = false } = normalized;
  try {
    const roomInfo = await getRoomId({
      room_type: ProjectType.Workflow,
    });
    const { parameter } = roomInfo.data;
    if (roomInfo?.data?.error_code === 0) {
      window.mtstat?.track('create_workflow', {
        location,
        room_id: parameter.room_id,
      });

      initMediaList?.length && sessionStorage.setItem(FLOW_INIT_MEDIA_KEY, JSON.stringify(initMediaList));

      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/ai_flow' : '/ai_flow',
        openNewWindow,
        query: {
          room_id: parameter.room_id,
        },
      });
    } else if (roomInfo?.data?.error_code === 5001) {
      const { toLogout } = useCommonStore.getState();
      toLogout();
      message.error('RoboNeo_Web_login_status_fail');
      setTimeout(() => window.location.reload(), 1000);
    }
    else {
      message.error('RoboNeo_Web_chat_send_fail');
    }
  } catch (error) {
    message.error('RoboNeo_Web_chat_send_fail');
  }
}

/**
 * 新建画布
 *
 * @export
 * @param {string} location
 * @return {*}
 */
export async function createNewCanvas(location: string) {
  const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
  const { toLogin } = useOfficialStore.getState();
  const { initUserInfo } = useCommonStore.getState();
  if (!isUserLoggedIn) {
    await toLogin().then(() => {
      initUserInfo();
    });
    createNewCanvasFun(location);
    return;
  }
  createNewCanvasFun(location);
}

/**
 * 新建画布
 *
 * @param {string} location
 */
async function createNewCanvasFun(location: string) {
  try {
    const roomInfo = await getRoomId({
      room_type: ProjectType.Canvas,
    });
    const { parameter } = roomInfo.data;
    if (roomInfo?.data?.error_code === 0) {
      window.mtstat?.track('create_conversation', {
        location,
        room_id: parameter.room_id,
      });
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
        query: {
          room_id: parameter.room_id,
        },
      });
    } else if (roomInfo?.data?.error_code === 5001) {
      const { toLogout } = useCommonStore.getState();
      toLogout();
      message.error('RoboNeo_Web_login_status_fail');
      setTimeout(() => window.location.reload(), 1000);
    }
    else {
      message.error('RoboNeo_Web_chat_send_fail');
    }
  } catch (error) {
    message.error('RoboNeo_Web_chat_send_fail');
  }
}

/**
 * 新建 Agent Teams（团队工作室）房间并跳转
 *
 * @param location - 埋点来源位置
 */
export async function createNewAgentTeams(location: string, type?: string) {
  const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
  const { toLogin } = useOfficialStore.getState();
  const { initUserInfo } = useCommonStore.getState();
  if (!isUserLoggedIn) {
    await toLogin().then(() => {
      initUserInfo();
    });
    createNewAgentTeamsFun(location, type);
    return;
  }
  createNewAgentTeamsFun(location, type);
}

/**
 * 创建团队工作室房间并导航至 team_studio
 *
 * @param location - 埋点来源位置
 */
async function createNewAgentTeamsFun(location: string, type?: string) {
  try {
    const roomInfo = await getRoomId({
      room_type: ProjectType.AgentTeam,
    });
    const { parameter } = roomInfo.data;
    if (roomInfo?.data?.error_code === 0) {
      window.mtstat?.track('create_conversation', {
        location,
        room_id: parameter.room_id,
        type,
      });
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/team_studio' : '/team_studio',
        query: {
          room_id: parameter.room_id,
        },
      });
    } else if (roomInfo?.data?.error_code === 5001) {
      const { toLogout } = useCommonStore.getState();
      toLogout();
      message.error('RoboNeo_Web_login_status_fail');
      setTimeout(() => window.location.reload(), 1000);
    }
    else {
      message.error('RoboNeo_Web_chat_send_fail');
    }
  } catch (error) {
    message.error('RoboNeo_Web_chat_send_fail');
  }
}
